import { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MapIcon from "@mui/icons-material/Map";
import CloseIcon from "@mui/icons-material/Close";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  isSameMonth,
  startOfToday,
  startOfWeek,
  differenceInDays,
} from "date-fns";
import useSupervisorCalendarData from "../../hooks/useSupervisorCalendarData";
import useSupervisorReps from "../../hooks/useSupervisorReps";
import useSupervisors from "../../hooks/useSupervisors";
import SkeletonDayCell from "./SkeletonDayCell";
import CallsPanel from "./CallsPanel";
import useLocalization from "../../hooks/useLocalization";
import useApi from "../../hooks/useApi";
import LocationsMap from "../elements/LocationsMap";
import { getId } from "../../utils/idHelpers";

const SupervisorCalendar = () => {
  const { getLabel } = useLocalization();
  const currentUserId = useSelector((state) => state.authentication.userId);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [panelCalls, setPanelCalls] = useState([]);
  const [panelLoading, setPanelLoading] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [mapDate, setMapDate] = useState(null);
  const [mapTitle, setMapTitle] = useState("");
  const [mapCalls, setMapCalls] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const { postData } = useApi();

  // Filters
  const [filters, setFilters] = useState({
    supervisorId: currentUserId, // Default to logged-in user
    repId: "all",
    clientId: "all",
    projectId: "all",
    status: "all",
  });

  const { supervisors, isLoading: supervisorsLoading } = useSupervisors();
  const { reps, isLoading: repsLoading } = useSupervisorReps(
    filters.supervisorId,
  );
  const { calendarData, loadingWeeks } = useSupervisorCalendarData(
    currentMonth,
    filters,
    filters.supervisorId,
  );

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({
    start: monthStart,
    end: monthEnd,
  });

  // Group calendar days by week
  const weeks = useMemo(() => {
    const weeks = [];
    let currentWeek = [];

    // Add empty cells for days before month starts
    const firstDayOfWeek = monthStart.getDay();
    for (let i = 0; i < firstDayOfWeek; i++) {
      currentWeek.push(null);
    }

    // Add days of month
    calendarDays.forEach((day) => {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        weeks.push([...currentWeek]);
        currentWeek = [];
      }
    });

    // Add empty cells for days after month ends
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push([...currentWeek]);
    }

    return weeks;
  }, [calendarDays, monthStart]);

  const getDayData = (day) => {
    if (!day) return null;
    const dateStr = format(day, "yyyy-MM-dd");
    return calendarData[dateStr];
  };

  const getWeekNumber = (day) => {
    if (!day) return 0;
    const weekStart = startOfWeek(day, { weekStartsOn: 0 });
    const monthWeekStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    return Math.floor(differenceInDays(weekStart, monthWeekStart) / 7);
  };

  const formatAddress = (address) => {
    if (!address) return "";
    if (typeof address === "string") return address;

    const parts = [
      address.streetAddress,
      address.city,
      address.province,
      address.postal,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const getCoordinateValue = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getLocationsForCalls = (calls) => {
    const grouped = {};

    calls.forEach((call) => {
      const locationId = call.locationId || getId(call.location);
      if (!locationId) return;

      const locationName = call.locationName || call.location?.name;
      const locationNumber =
        call.locationNumber || call.location?.locationNumber;
      const locationAddress = formatAddress(
        call.address || call.location?.address,
      );
      const latitude = getCoordinateValue(call.address?.latitude);
      const longitude = getCoordinateValue(call.address?.longitude);

      if (!grouped[locationId]) {
        grouped[locationId] = {
          id: locationId,
          name: locationName,
          number: locationNumber,
          address: locationAddress,
          latitude,
          longitude,
          calls: [],
        };
      }

      grouped[locationId].calls.push({
        id: getId(call) || call.id,
        scheduleDate: call.scheduleDate || call.yyyymmdd,
        projectName: call.projectName,
        status: call.callStatus,
      });
    });

    return Object.values(grouped);
  };

  const handlePrevMonth = () => setCurrentMonth(addMonths(currentMonth, -1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const handleToday = () => setCurrentMonth(startOfToday());

  const handleSelectDate = (day) => {
    if (day) {
      setSelectedDate(day);
      fetchCallsForDate(day);
      setIsPanelOpen(true);
    }
  };

  const handleOpenMap = (day) => {
    if (!day) return;
    setMapDate(day);
    setMapTitle(`Map for ${format(day, "MMM d, yyyy")}`);
    fetchMapCallsForDate(day);
    setIsMapOpen(true);
  };

  const handleCloseMap = () => {
    setIsMapOpen(false);
    setMapDate(null);
    setMapCalls([]);
  };

  const handleClosePanel = () => {
    setIsPanelOpen(false);
    setSelectedDate(null);
    setPanelCalls([]);
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
      // When supervisor changes, reset repId since rep list will change
      ...(filterKey === "supervisorId" && { repId: "all" }),
    }));
  };

  // Extract unique clients and projects from calendar data
  const uniqueClients = useMemo(() => {
    const clients = new Map();
    Object.values(calendarData).forEach((dayData) => {
      if (dayData && dayData.projects) {
        dayData.projects.forEach((project) => {
          if (project.clientId && project.clientName) {
            clients.set(project.clientId, project.clientName);
          }
        });
      }
    });
    return Array.from(clients.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [calendarData]);

  const uniqueProjects = useMemo(() => {
    const projects = new Map();
    Object.values(calendarData).forEach((dayData) => {
      if (dayData && dayData.projects) {
        dayData.projects.forEach((project) => {
          // Only include projects for the selected client
          if (
            filters.clientId === "all" ||
            project.clientId === filters.clientId
          ) {
            if (project.projectId && project.projectName) {
              projects.set(project.projectId, project.projectName);
            }
          }
        });
      }
    });
    return Array.from(projects.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [calendarData, filters.clientId]);

  const fetchCallsForDate = async (day) => {
    if (!day) return;
    setPanelLoading(true);
    try {
      const dateStr = format(day, "yyyy-MM-dd");
      const result = await postData({
        entityName: "Call",
        action: "getSupervisorCallsForDate",
        data: {
          date: dateStr,
          repId: filters.repId !== "all" ? filters.repId : undefined,
          clientId: filters.clientId !== "all" ? filters.clientId : undefined,
          projectId:
            filters.projectId !== "all" ? filters.projectId : undefined,
          status: filters.status !== "all" ? filters.status : undefined,
          ...(filters.supervisorId && { supervisorId: filters.supervisorId }),
        },
      });
      setPanelCalls(result?.data?.calls || []);
    } catch (error) {
      console.error("Error fetching calls:", error);
    } finally {
      setPanelLoading(false);
    }
  };

  const fetchMapCallsForDate = async (day) => {
    if (!day) return;
    setMapLoading(true);
    try {
      const dateStr = format(day, "yyyy-MM-dd");
      const result = await postData({
        entityName: "Call",
        action: "getSupervisorCallsForDate",
        data: {
          date: dateStr,
          repId: filters.repId !== "all" ? filters.repId : undefined,
          clientId: filters.clientId !== "all" ? filters.clientId : undefined,
          projectId:
            filters.projectId !== "all" ? filters.projectId : undefined,
          status: filters.status !== "all" ? filters.status : undefined,
          ...(filters.supervisorId && { supervisorId: filters.supervisorId }),
        },
      });
      setMapCalls(result?.data?.calls || []);
    } catch (error) {
      console.error("Error fetching map calls:", error);
    } finally {
      setMapLoading(false);
    }
  };

  const isLoading = repsLoading || supervisorsLoading;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        maxHeight: "100%",
        width: "100%",
        maxWidth: "100vw",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Filter Bar */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 1,
          marginBottom: 2,
          flexShrink: 0,
        }}
      >
        <FormControl size="small">
          <InputLabel>{getLabel("supervisor") || "Supervisor"}</InputLabel>
          <Select
            label={getLabel("supervisor") || "Supervisor"}
            value={filters.supervisorId}
            onChange={(e) => handleFilterChange("supervisorId", e.target.value)}
          >
            {supervisors.map((supervisor) => (
              <MenuItem
                key={supervisor._id || supervisor.id}
                value={supervisor._id || supervisor.id}
              >
                {supervisor.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <InputLabel>{getLabel("rep") || "Rep"}</InputLabel>
          <Select
            label={getLabel("rep") || "Rep"}
            value={filters.repId}
            onChange={(e) => handleFilterChange("repId", e.target.value)}
          >
            <MenuItem value="all">All Reps</MenuItem>
            {reps.map((rep) => (
              <MenuItem
                key={rep._id || rep.id}
                value={rep._id || rep.id}
              >
                {rep.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <InputLabel>{getLabel("client") || "Client"}</InputLabel>
          <Select
            label={getLabel("client") || "Client"}
            value={filters.clientId}
            onChange={(e) => handleFilterChange("clientId", e.target.value)}
          >
            <MenuItem value="all">All Clients</MenuItem>
            {uniqueClients.map((client) => (
              <MenuItem
                key={client.id}
                value={client.id}
              >
                {client.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <InputLabel>{getLabel("project") || "Project"}</InputLabel>
          <Select
            label={getLabel("project") || "Project"}
            value={filters.projectId}
            onChange={(e) => handleFilterChange("projectId", e.target.value)}
          >
            <MenuItem value="all">All Projects</MenuItem>
            {uniqueProjects.map((project) => (
              <MenuItem
                key={project.id}
                value={project.id}
              >
                {project.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <InputLabel>{getLabel("status") || "Status"}</InputLabel>
          <Select
            label={getLabel("status") || "Status"}
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="future">Future</MenuItem>
            <MenuItem value="incomplete">Incomplete</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Header with Month Navigation */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: 2, flexShrink: 0 }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600 }}
        >
          {format(currentMonth, "MMMM yyyy")}
        </Typography>
        <Stack
          direction="row"
          gap={1}
        >
          <Button
            startIcon={<ChevronLeftIcon />}
            onClick={handlePrevMonth}
            variant="outlined"
            size="small"
          >
            Prev
          </Button>
          <Button
            onClick={handleToday}
            variant="outlined"
            size="small"
          >
            Today
          </Button>
          <Button
            endIcon={<ChevronRightIcon />}
            onClick={handleNextMonth}
            variant="outlined"
            size="small"
          >
            Next
          </Button>
        </Stack>
      </Stack>

      {/* Weekday Headers */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.5,
          marginBottom: 1,
          flexShrink: 0,
        }}
      >
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <Box key={day}>
            <Typography
              align="center"
              sx={{ fontWeight: 600, color: "#666", fontSize: "0.875rem" }}
            >
              {day}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Calendar Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))`,
          gap: 0.25,
          flex: 1,
          minHeight: 0,
          maxHeight: "100%",
          overflow: "auto",
        }}
      >
        {weeks.map((week, weekIdx) =>
          week.map((day, dayIdx) => {
            const dayData = getDayData(day);
            const weekNum = getWeekNumber(day);
            const isLoading = loadingWeeks[weekNum];
            const isToday =
              day &&
              isSameMonth(day, new Date()) &&
              format(day, "d") === format(new Date(), "d");

            if (isLoading) {
              return (
                <Box key={`${weekIdx}-${dayIdx}`}>
                  <SkeletonDayCell />
                </Box>
              );
            }

            return (
              <Box
                key={`${weekIdx}-${dayIdx}`}
                sx={{ minHeight: 0, minWidth: 0, height: "100%" }}
              >
                <Card
                  onClick={() => handleSelectDate(day)}
                  sx={{
                    padding: 1,
                    height: "100%",
                    overflow: "auto",
                    minWidth: 0,
                    cursor: day ? "pointer" : "default",
                    backgroundColor: isToday ? "#f0f7ff" : "#fafafa",
                    border: isToday ? "2px solid #1976d2" : "1px solid #e0e0e0",
                    borderRadius: 1,
                    transition: "all 0.2s",
                    "&:hover": day
                      ? { boxShadow: 2, backgroundColor: "#f5f5f5" }
                      : {},
                  }}
                >
                  {day ? (
                    <>
                      {/* Day Number + Map */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            color: isToday ? "#1976d2" : "#333",
                          }}
                        >
                          {format(day, "d")}
                        </Typography>
                        {dayData?.total > 0 && (
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenMap(day);
                            }}
                            sx={{ ml: 1 }}
                            aria-label="Open map"
                          >
                            <MapIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {/* Total Call Count */}
                      {dayData && (
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: "#999",
                            marginTop: 0.5,
                          }}
                        >
                          {dayData.total} calls
                        </Typography>
                      )}

                      {/* Projects List */}
                      {dayData && dayData.projects.length > 0 && (
                        <Box
                          sx={{
                            marginTop: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                          }}
                        >
                          {dayData.projects.map((project) => (
                            <Box key={project.projectId}>
                              {/* Project Name */}
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#333",
                                  whiteSpace: "normal",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                  lineHeight: 1.2,
                                }}
                              >
                                {project.projectName || "Unnamed Project"}
                              </Typography>

                              {/* Call Count */}
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  color: "#666",
                                  marginTop: 0.25,
                                }}
                              >
                                {project.callCount} calls
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </>
                  ) : null}
                </Card>
              </Box>
            );
          }),
        )}
      </Box>

      {/* Calls Panel Slider */}
      {isPanelOpen && selectedDate && (
        <CallsPanel
          date={selectedDate}
          calls={panelCalls}
          onClose={handleClosePanel}
          onRefresh={() => {
            // Trigger refresh if needed
            fetchCallsForDate(selectedDate);
          }}
          isSupervisor={true}
          reps={reps}
          supervisorId={filters.supervisorId}
        />
      )}

      <Dialog
        open={isMapOpen}
        onClose={handleCloseMap}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {mapTitle}
          <IconButton
            onClick={handleCloseMap}
            sx={{ position: "absolute", right: 8, top: 8 }}
            aria-label="Close map"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ height: "70vh", p: 2 }}>
          {mapLoading ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: "100%" }}>
              <LocationsMap
                locations={getLocationsForCalls(mapCalls)}
                selectedLocationId={null}
                hoveredLocationId={null}
                onSelectLocation={null}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SupervisorCalendar;
